/**
 * Имена разделов приложения: один источник для заголовка шапки и для `document.title`
 * (находки QA-15 и U15 от 15.09.2026 — часть страниц держала в заголовке вкладки название
 * предыдущего раздела, латиницу («:: Home», «:: Profile») или ничего, а на `/notifications`
 * и `/subscription` заголовок шапки был пуст).
 *
 * Таблица покрывает все маршруты приложения, включая те, которых нет в меню (уведомления,
 * подписка, профиль, конструктор образа, контур авторизации). Порядок записей значения не
 * имеет — поиск идёт от самого длинного совпадения, чтобы `/outfits/builder` не уехал
 * в `/outfit`; ниже маршруты сгруппированы по разделам меню для читаемости.
 */
export interface PageTitleRoute {
    /** Префикс пути, например `/outfits/saved`. */
    key: string;
    /** Имя раздела для шапки и заголовка вкладки. */
    label: string;
}

export const PAGE_TITLE_ROUTES: PageTitleRoute[] = [
    // Гардероб
    {key: '/clothing', label: 'Все вещи'},
    {key: '/pool', label: 'Активный пул'},
    {key: '/boxes', label: 'Коробки'},
    {key: '/lists', label: 'Списки'},
    {key: '/disposal', label: 'Выбытие'},
    {key: '/trips', label: 'Поездки'},
    {key: '/wardrobes', label: 'Доступные гардеробы'},
    // Образы
    {key: '/outfits/saved', label: 'Каталог образов'},
    {key: '/outfits/builder', label: 'Конструктор образа'},
    {key: '/outfit-evaluation', label: 'Оценка образа'},
    {key: '/outfit', label: 'Образ дня'},
    // Помощник
    {key: '/stylist', label: 'AI-стилист'},
    {key: '/recommendations', label: 'Рекомендации'},
    {key: '/ai-recognition', label: 'AI распознавание'},
    {key: '/indexing', label: 'Индексация'},
    {key: '/purchase-analysis', label: 'Анализ покупки'},
    {key: '/strategy', label: 'Стратегия'},
    {key: '/onboarding', label: 'Настройка стилиста'},
    // Аналитика
    {key: '/insights', label: 'Аналитика'},
    {key: '/wardrobe-value', label: 'Стоимость'},
    // Помощник и сервис
    {key: '/shopping', label: 'Список покупок'},
    {key: '/football', label: 'Футбольная форма'},
    // Личное
    {key: '/home', label: 'Главная'},
    {key: '/notifications', label: 'Уведомления'},
    {key: '/subscription', label: 'Подписка и лимиты'},
    {key: '/settings/profile', label: 'Настройки'},
    {key: '/settings', label: 'Настройки'},
    {key: '/profile', label: 'Настройки'},
    {key: '/admin', label: 'Админ-панель'},
    // Публичный контур
    {key: '/login', label: 'Вход'},
    {key: '/register', label: 'Регистрация'},
    {key: '/verify-email', label: 'Подтверждение почты'},
    {key: '/forgot-password', label: 'Восстановление пароля'},
    {key: '/reset-password', label: 'Новый пароль'},
    {key: '/legal', label: 'Правовые документы'},
];

/** Маршруты, отсортированные от самого специфичного к общему — порядок поиска. */
const ROUTES_BY_SPECIFICITY = [...PAGE_TITLE_ROUTES].sort((a, b) => b.key.length - a.key.length);

/**
 * Имя раздела по пути. Пустая строка — раздела нет (лендинг `/`, неизвестный путь):
 * вызывающий сам решает, что показать (404 подставляет своё имя).
 */
export function findPageName(pathname: string): string {
    const path = pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
    const match = ROUTES_BY_SPECIFICITY.find(r => path === r.key || path.startsWith(r.key + '/'));
    return match ? match.label : '';
}
